import { EVENTBRITE_CATEGORY_TO_REFINEMENT } from '@eventbrite/categories';
import { Icon } from '@eventbrite/eds-icon';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { BucketHeader } from '../../../../components/BucketHeader';
import { SeeMoreButton } from '../../../../components/SeeMoreButton';
import {
    CATEGORY_NAME_ICON_MAP,
    CATEGORY_TEMPLATE,
} from '../../../../constants';
import { useEnvContext } from '../../../../context';
import { FeatureFlags } from '../../../../types';
import './categoryBucket.scss';

const Category = ({
    name,
    url,
    isCarousel,
    iconType,
    enableClientSideLinking,
}: {
    url: string;
    name?: string;
    isCarousel?: boolean;
    iconType: React.ReactNode;
    enableClientSideLinking?: boolean;
}) => {
    if (!url) {
        return null;
    }

    const classes = classNames('eds-link--neutral', 'category-item', {
        'category-item--mobile': isCarousel,
        'eds-g-cell': !isCarousel,
        'eds-g-cell-2-12': !isCarousel,
    });

    const content = (
        <>
            <div className="eds-align--center">
                <Icon type={iconType} size="medium" hasHalo={true} />
            </div>
            <div className="eds-text--center eds-l-pad-top-4">{name}</div>
        </>
    );

    return enableClientSideLinking ? (
        <span className={classes}>
            <Link to={url}>{content}</Link>
        </span>
    ) : (
        <span className={classes}>
            <a href={url}>{content}</a>
        </span>
    );
};

const _getRenderedCategories = (
    categories: { url?: string; name?: string; id?: number }[],
    isCarousel?: boolean,
    enableClientSideLinking?: boolean,
    locationSlug?: string,
    uses_language_subdirectory?: boolean,
    locale?: string,
) =>
    categories.map((category) => {
        const { url = '', name, id } = category;
        const iconType = CATEGORY_NAME_ICON_MAP[id || 0];

        let linkUrl = url;

        if (enableClientSideLinking) {
            const categoryId = `EventbriteCategory/${id}`;
            const categoryRefinment =
                EVENTBRITE_CATEGORY_TO_REFINEMENT[categoryId];

            if (uses_language_subdirectory) {
                linkUrl = `/${locale}/b/${locationSlug}/${categoryRefinment}/`;
            } else {
                linkUrl = `/b/${locationSlug}/${categoryRefinment}/`;
            }
        }

        if (!iconType) {
            return null;
        }

        return (
            <Category
                name={name}
                url={linkUrl}
                key={id}
                iconType={iconType}
                isCarousel={isCarousel}
                enableClientSideLinking={enableClientSideLinking}
            />
        );
    });

const CategoryGroup = ({
    categories,
    seeMoreText,
    seeMoreUrl,
    title,
    enableClientSideLinking,
    locationSlug,
}: {
    categories: { url: string; name: string; id: number }[];
    seeMoreText?: string;
    seeMoreUrl: string;
    title: string | GenericLazyString;
    enableClientSideLinking?: boolean;
    locationSlug?: string;
}) => {
    const { env } = useEnvContext();
    const { uses_language_subdirectory = false, locale } =
        env?.localeInfo || {};
    const isCarousel = categories.length > 6;
    const classes = classNames('eds-show-up-md', {
        'mobile-card-carousel': isCarousel,
        'eds-g-group': !isCarousel,
    });

    return (
        <div className="eds-l-mar-top-6 eds-l-mn-mar-top-10 eds-l-md-mar-top-10 eds-l-mw-mar-top-10 eds-l-ln-mar-top-10 eds-l-lg-mar-top-10 eds-l-lw-mar-top-10">
            <div className={classes}>
                {_getRenderedCategories(
                    categories,
                    isCarousel,
                    enableClientSideLinking,
                    locationSlug,
                    uses_language_subdirectory,
                    locale,
                )}
            </div>
            <div className="mobile-card-carousel eds-show-down-mn">
                {_getRenderedCategories(
                    categories,
                    true,
                    enableClientSideLinking,
                    locationSlug,
                    uses_language_subdirectory,
                    locale,
                )}
            </div>
            <div className="eds-l-pad-top-6">
                <div className="eds-show-down-sn eds-align--center">
                    <SeeMoreButton
                        title={title}
                        linkUrl={seeMoreUrl}
                        redirectOnClick={true}
                        seeMoreText={seeMoreText}
                    />
                </div>
            </div>
        </div>
    );
};

interface CategoryBucketProps {
    /*
     * Array of preformatted objects that will represent that entities within the bucket
     */
    content: {
        id: number;
        name: string;
        url: string;
    }[];
    /*
     * The name of the given bucket ie. "Upcoming Music Events"
     */
    name: string;
    /*
     * Url for all events directory page
     */
    allEventsUrl?: string;
    /**
     * Feature flags object
     */
    featureFlags?: FeatureFlags;
    /**
     * url location slug
     */
    locationSlug?: string;
}

export const CategoryBucket = ({
    content,
    name,
    allEventsUrl,
    featureFlags,
    locationSlug,
}: CategoryBucketProps) => {
    const seeMoreText = gettext('Search all events').toString();
    const enableClientSideLinking = featureFlags?.indexCategoryBrowse;

    return (
        <div>
            <BucketHeader
                title={name}
                linkUrl={allEventsUrl}
                seeMoreText={seeMoreText}
                template={CATEGORY_TEMPLATE}
            />
            <CategoryGroup
                categories={content}
                title={name}
                seeMoreUrl={allEventsUrl || ''}
                seeMoreText={seeMoreText}
                enableClientSideLinking={enableClientSideLinking}
                locationSlug={locationSlug}
            />
        </div>
    );
};
